import React, { useState, useEffect, useRef } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import DropDownListComponent from "@CubeComponents/form/DropdownListComponent";
import MultiSelectComponent from "@CubeComponents/form/MultiselectComponent";
import RadioButtonComponent from "@CubeComponents/form/RadioButtonComponent";
import CheckboxComponent from "@CubeComponents/form/CheckboxComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { useTranslation } from "react-i18next";

interface ChecklistAssignmentModalProps {
  reviewData: any;
  setReviewData: (data: any) => void;
  onNext: (data: any) => void;
  onBack: () => void;
}

const ChecklistAssignmentModal: React.FC<ChecklistAssignmentModalProps> = ({
  reviewData,
  setReviewData,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation();

  const [applyToAllFiles, setApplyToAllFiles] = useState<boolean>(true);
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [globalAssignments, setGlobalAssignments] = useState<any>({
    list: [],
    selected: [],
    showAddRow: false,
  });

  const [fileAssignments, setFileAssignments] = useState<any>({});

  // For add row form
  const [addingToFileId, setAddingToFileId] = useState<string | null>(null);
  const [selectedChecklistId, setSelectedChecklistId] = useState<string>("");
  const [selectedLevelIds, setSelectedLevelIds] = useState<any[]>([]);

  const checklistRef = useRef<any>(null);

  // Load assignments from reviewData
  useEffect(() => {
    // Load global assignments
    setGlobalAssignments({
      list: reviewData?.checklistAssignments || [],
      selected: [],
      showAddRow: false,
    });

    const perFile: any = {};
    reviewData?.resources?.forEach((file: any) => {
      perFile[file.fileVersionId] = {
        list: file.checklistAssignments || [],
        selected: [],
        showAddRow: false,
      };
    });
    setFileAssignments(perFile);

    if (!applyToAllFiles && !isSaving && reviewData?.resources?.length > 0) {
      setExpandedFileId(reviewData.resources[0].fileVersionId);
    }
  }, [reviewData?.resources, reviewData?.checklistAssignments, applyToAllFiles]);

  function showAddRowGlobal() {
    setGlobalAssignments((prev: any) => ({ ...prev, showAddRow: true }));
    setAddingToFileId("global");
    setSelectedChecklistId("");
    setSelectedLevelIds([]);
  }

  function showAddRowForFile(fileId: string) {
    setFileAssignments((prev: any) => ({
      ...prev,
      [fileId]: { ...prev[fileId], showAddRow: true },
    }));
    setAddingToFileId(fileId);
    setSelectedChecklistId("");
    setSelectedLevelIds([]);
  }

  useEffect(() => {
    if (!addingToFileId || !selectedChecklistId || selectedLevelIds.length === 0) return;

    const checklist = reviewData.workflow.checklists.find((c: any) => c._id === selectedChecklistId);
    const levels = reviewData.workflow.reviewLevels.filter((l: any) => selectedLevelIds.includes(l._id));

    if (!checklist || levels.length === 0) return;

    const newAssignment = {
      checklistId: checklist._id,
      checklistName: checklist.title,
      levelIds: levels.map((l: any) => l._id),
      levelNames: levels.map((l: any) => l.title),
    };

    if (addingToFileId === "global") {
      setGlobalAssignments((prev: any) => {
        const updated = [...prev.list];
        const existingIndex = updated.findIndex((a: any) => a.checklistId === checklist._id);

        if (existingIndex >= 0) {
          updated[existingIndex] = newAssignment;
        } else {
          updated.push(newAssignment);
        }

        return {
          ...prev,
          list: updated,
          showAddRow: false,
        };
      });
    } else {
      setFileAssignments((prev: any) => {
        const current = prev[addingToFileId];
        const updated = [...current.list];
        const existingIndex = updated.findIndex((a: any) => a.checklistId === checklist._id);

        if (existingIndex >= 0) {
          updated[existingIndex] = newAssignment;
        } else {
          updated.push(newAssignment);
        }

        return {
          ...prev,
          [addingToFileId]: {
            ...current,
            list: updated,
            showAddRow: false,
          },
        };
      });
    }

    setAddingToFileId(null);
    setSelectedChecklistId("");
    setSelectedLevelIds([]);
  }, [addingToFileId, selectedChecklistId, selectedLevelIds, reviewData.workflow]);

  function toggleRowGlobal(checklistId: string) {
    setGlobalAssignments((prev: any) => {
      const updated = prev.selected.includes(checklistId)
        ? prev.selected.filter((id: string) => id !== checklistId)
        : [...prev.selected, checklistId];

      return { ...prev, selected: updated };
    });
  }

  function toggleRowForFile(fileId: string, checklistId: string) {
    setFileAssignments((prev: any) => {
      const current = prev[fileId].selected;
      const updated = current.includes(checklistId)
        ? current.filter((id: string) => id !== checklistId)
        : [...current, checklistId];

      return {
        ...prev,
        [fileId]: { ...prev[fileId], selected: updated },
      };
    });
  }

  function toggleAllGlobal() {
    setGlobalAssignments((prev: any) => {
      const allIds = prev.list.map((a: any) => a.checklistId);
      const allSelected = prev.selected.length === allIds.length;
      return { ...prev, selected: allSelected ? [] : allIds };
    });
  }

  function toggleAllForFile(fileId: string) {
    setFileAssignments((prev: any) => {
      const allIds = prev[fileId].list.map((a: any) => a.checklistId);
      const allSelected = prev[fileId].selected.length === allIds.length;

      return {
        ...prev,
        [fileId]: { ...prev[fileId], selected: allSelected ? [] : allIds },
      };
    });
  }

  function removeSelectedGlobal() {
    setGlobalAssignments((prev: any) => {
      const updated = prev.list.filter((a: any) => !prev.selected.includes(a.checklistId));
      return { ...prev, list: updated, selected: [] };
    });
  }

  function removeSelectedFromFile(fileId: string) {
    setFileAssignments((prev: any) => {
      const current = prev[fileId];
      const updated = current.list.filter((a: any) => !current.selected.includes(a.checklistId));

      return {
        ...prev,
        [fileId]: { ...prev[fileId], list: updated, selected: [] },
      };
    });
  }

  async function handleSave() {
    setIsSaving(true);

    let updatedFiles: any[];

    if (applyToAllFiles) {
      const assignments = globalAssignments.list.map((a: any) => ({
        checklistId: a.checklistId,
        levelIds: a.levelIds || [],
      }));

      updatedFiles = reviewData.resources.map((file: any) => ({
        ...file,
        checklistAssignments: assignments,
      }));
    } else {
      updatedFiles = reviewData.resources.map((file: any) => {
        const fileList = fileAssignments[file.fileVersionId]?.list || [];
        const assignments = fileList.map((a: any) => ({
          checklistId: a.checklistId,
          levelIds: a.levelIds || [],
        }));

        return {
          ...file,
          checklistAssignments: assignments,
        };
      });
    }

    const updated = {
      ...reviewData,
      resources: updatedFiles,
    };

    setReviewData(updated);
    onNext(updated);
  }

  function renderGlobalTable() {
    const data = globalAssignments;
    const levelOptions = (reviewData?.workflow?.reviewLevels || []).map((level: any) => ({
      ...level,
      serialLabel: `Level ${level.serial}`,
    }));

    return (
      <div className="w-full border border-[#E3EAF6] rounded-md overflow-hidden mt-2">
        <div className="flex gap-2 p-2 bg-[#F3F7FD] border-b border-[#E3EAF6]">
          <ButtonComponent
            startIcon={<AddRoundedIcon />}
            className="px-4 py-1.5 rounded-md bg-[#EAF2FF] border border-[#D0E2FF] hover:bg-[#DCE9FF] text-sm font-medium"
            onClick={showAddRowGlobal}
          >
            {t("Add")}
          </ButtonComponent>
          <ButtonComponent
            startIcon={<DeleteIcon size={16} className={""} />}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${
              data.selected.length > 0
                ? "bg-transparent text-[#6B7280] hover:text-[#374151]"
                : "bg-transparent text-gray-300 cursor-not-allowed"
            }`}
            disabled={data.selected.length === 0}
            onClick={removeSelectedGlobal}
          >
            {t("Remove")}
          </ButtonComponent>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-[#F3F7FD] text-gray-700 text-xs uppercase">
            <tr>
              <th className="p-2 w-10 text-center">
                <CheckboxComponent
                  checked={data.list.length > 0 && data.selected.length === data.list.length}
                  onChange={toggleAllGlobal}
                />
              </th>
              <th className="text-start p-2 font-semibold">{t("Checklist")}</th>
              <th className="text-start p-2 font-semibold">{t("Level")}</th>
            </tr>
          </thead>
          <tbody>
            {data.list.map((assignment: any) => (
              <tr key={assignment.checklistId} className="hover:bg-gray-50">
                <td className="text-center p-2">
                  <CheckboxComponent
                    checked={data.selected.includes(assignment.checklistId)}
                    onChange={() => toggleRowGlobal(assignment.checklistId)}
                  />
                </td>
                <td className="p-2">{assignment.checklistName}</td>
                <td className="p-2">{assignment.levelNames?.join(", ")}</td>
              </tr>
            ))}

            {data.showAddRow && (
              <tr className="bg-gray-50">
                <td></td>
                <td className="p-2">
                  <DropDownListComponent
                    ref={checklistRef}
                    dataSource={reviewData.workflow.checklists || []}
                    fields={{ text: "title", value: "_id" }}
                    placeholder={t("Select Checklist")}
                    change={(e: any) => setSelectedChecklistId(e.value)}
                    value={selectedChecklistId}
                  />
                </td>
                <td className="p-2">
                  <MultiSelectComponent
                    dataSource={levelOptions}
                    fields={{ text: "serialLabel", value: "_id" }}
                    placeholder={t("Select Level(s)")}
                    value={selectedLevelIds}
                    change={(e: any) => setSelectedLevelIds(e.value)}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="text-xs text-gray-600 p-3 border-t border-gray-100">
          {data.list.length} {t("rows")}
        </div>
      </div>
    );
  }

  function renderFileTable(fileId: string) {
    const data = fileAssignments[fileId] || { list: [], selected: [], showAddRow: false };
    const levelOptions = (reviewData?.workflow?.reviewLevels || []).map((level: any) => ({
      ...level,
      serialLabel: `Level ${level.serial}`,
    }));

    return (
      <div className="w-full border border-[#E3EAF6] rounded-md overflow-hidden mt-2">
        <div className="flex gap-2 p-2 bg-[#F3F7FD] border-b border-[#E3EAF6]">
          <ButtonComponent
            startIcon={<AddRoundedIcon />}
            className="px-4 py-1.5 rounded-md bg-[#EAF2FF] border border-[#D0E2FF] hover:bg-[#DCE9FF] text-sm font-medium"
            onClick={() => showAddRowForFile(fileId)}
          >
            {t("Add")}
          </ButtonComponent>
          <ButtonComponent
            startIcon={<DeleteIcon size={16} className={""} />}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${
              data.selected.length > 0
                ? "bg-transparent text-[#6B7280] hover:text-[#374151]"
                : "bg-transparent text-gray-300 cursor-not-allowed"
            }`}
            disabled={data.selected.length === 0}
            onClick={() => removeSelectedFromFile(fileId)}
          >
            {t("Remove")}
          </ButtonComponent>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-[#F3F7FD] text-gray-700 text-xs uppercase">
            <tr>
              <th className="p-2 w-10 text-center">
                <CheckboxComponent
                  checked={data.list.length > 0 && data.selected.length === data.list.length}
                  onChange={() => toggleAllForFile(fileId)}
                />
              </th>
              <th className="text-start p-2 font-semibold">{t("Checklist")}</th>
              <th className="text-start p-2 font-semibold">{t("Level")}</th>
            </tr>
          </thead>
          <tbody>
            {data.list.map((assignment: any) => (
              <tr key={assignment.checklistId} className="hover:bg-gray-50">
                <td className="text-center p-2">
                  <CheckboxComponent
                    checked={data.selected.includes(assignment.checklistId)}
                    onChange={() => toggleRowForFile(fileId, assignment.checklistId)}
                  />
                </td>
                <td className="p-2">{assignment.checklistName}</td>
                <td className="p-2">{assignment.levelNames?.join(", ")}</td>
              </tr>
            ))}

            {data.showAddRow && (
              <tr className="bg-gray-50">
                <td></td>
                <td className="p-2">
                  <DropDownListComponent
                    ref={checklistRef}
                    dataSource={reviewData.workflow.checklists || []}
                    fields={{ text: "title", value: "_id" }}
                    placeholder={t("Select Checklist")}
                    change={(e: any) => setSelectedChecklistId(e.value)}
                    value={selectedChecklistId}
                  />
                </td>
                <td className="p-2">
                  <MultiSelectComponent
                    dataSource={levelOptions}
                    fields={{ text: "serialLabel", value: "_id" }}
                    placeholder={t("Select Level(s)")}
                    value={selectedLevelIds}
                    change={(e: any) => setSelectedLevelIds(e.value)}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Footer */}
        <div className="text-xs text-gray-600 p-3 border-t border-gray-100">
          {data.list.length} {t("rows")}
        </div>
      </div>
    );
  }

  return (
    <ModalStateDialogComponent
      isVisible
      allowModalPadding={false}
      width="100%"
      zIndex={30000}
      headerTitle={
        <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold">
          <ArrowBackIosNewIcon onClick={onBack} sx={{ fontSize: 18, cursor: "pointer", color: "#4B5563" }} />
          {t("Assign Checklist")}
        </div>
      }
      modelContent={
        <div className="flex flex-col max-h-[75vh] overflow-y-auto p-5">
          <div className="mb-4 text-sm flex flex-col space-y-3">
            <RadioButtonComponent
              name="mode"
              value="all"
              label={t("Assign same Checklists to all files")}
              checked={applyToAllFiles}
              change={() => setApplyToAllFiles(true)}
            />
            <RadioButtonComponent
              name="mode"
              value="individual"
              label={t("Assign different Checklists to each file")}
              checked={!applyToAllFiles}
              change={() => setApplyToAllFiles(false)}
            />
          </div>

          {/* Tables */}
          {applyToAllFiles ? (
            <div>{renderGlobalTable()}</div>
          ) : (
            reviewData.resources?.map((file: any) => (
              <Accordion
                key={file.fileVersionId}
                expanded={expandedFileId === file.fileVersionId}
                onChange={(e: any, expanded: boolean) => setExpandedFileId(expanded ? file.fileVersionId : null)}
                sx={{
                  border: "1px solid #E3EAF6",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  mb: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ fontSize: 20 }} />}
                  className="bg-white border-b border-[#E3EAF6] px-4 py-2"
                >
                  <span className="font-medium text-gray-800">{file.name}</span>
                </AccordionSummary>
                <AccordionDetails className="p-0">{renderFileTable(file.fileVersionId)}</AccordionDetails>
              </Accordion>
            ))
          )}
        </div>
      }
      footerContent={
        <div className="flex gap-2 justify-end">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleSave} loading={isSaving}>
            {t("Assign")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onBack}>
            {t("Back")}
          </ButtonComponent>
        </div>
      }
      onCancel={onBack}
    />
  );
};

export default ChecklistAssignmentModal;
